<template>
  <div
    class="BreadCrumbs__Wrapper items-center text-sm text-gray-500 | hidden | xl:flex mt-0.5"
    ref="crumbs"
  >
    <template v-for="(crumb, crumbIndex) in crumbs" :key="crumbIndex">
      <div class="Chunk flex items-center">
        <span class="mx-2" v-if="crumbIndex">/</span>
        <a 
          :href="crumb.url" 
          class="flex items-center hover:text-gray-800"
          @click="crumbClick(crumbIndex === 0)"
        >
          <aw-icon
            v-if="crumbIndex === 0"
            icon="home"
            class="BreadCrumbs__HomeIcon w-4 h-4 mr-1.5"
          />
          {{ crumb.title }}
        </a>
      </div>
    </template>
    <span class="mx-2 inline-flex relative">
      <aw-icon
        :icon="icon"
        class="w-4 h-4 ml-1.5 "
        :class="iconColor"
        v-if="icon"
      ></aw-icon>
    </span>
  </div>

  <!-- mobile -->
  <div class="items-center text-gray-500 text-sm | flex | xl:hidden truncate flex-shrink">
    <a
      :href="mobileCrumb.url"
      class="inline-flex items-center hover:text-gray-800"
      @click="crumbClick(mobileCrumb.icon === 'home')"
    >
      <aw-icon
        :icon="mobileCrumb.icon"
        class="w-6 h-6 mr-2"
      />
    </a>
    <span v-if="!bcHomeStore.view">
      {{ mobileCrumb.title }}
    </span>
    <span
      v-if="icon"
      class="mx-2 inline-flex relative"
    >
      <aw-icon
        :icon="icon"
        class="w-4 h-4 ml-1.5 "
        :class="iconColor"
      />
    </span>
  </div>

  <div 
    v-if="bcHomeStore.view"
    class="hidden md:block ml-4 xl:ml-0 mt-0.5" 
  >
    <nav class="p-0 inline-flex">
      <template v-for="(item, index) in bcHomeStore.views" :key="item.name">
        <a
          class="pill__Tab pill__Tab-sm"
          :class="{ 'pill__Tab-active': item.name == bcHomeStore.view, 'mx-1': index > 0, 'mr-1': index === 0 }"
          href="#"
          @click.prevent="bcHomeStore.view = item.name"
        >
          {{ item.title }}
          <span
            v-if="item.name === 'for-approval'"
            :class="{ 'text-bc-blue': item.name !== bcHomeStore.view}"
          >
             &nbsp;({{ item.numOfItems }})
          </span>
        </a>
      </template>
    </nav>
  </div>

  <div 
    v-else-if="trashTabActive ||trashFileTabActive"
    class="hidden md:block ml-4 xl:ml-0 mt-0.5" 
  >
    <nav class="p-0 inline-flex">
      <a
        class="pill__Tab pill__Tab-sm mr-1"
        :class="{ 'pill__Tab-active': trashTabActive }"
        href="/trash/0/0"
      >
        {{ $t('Documents') }}
      </a>
      <a
        class="pill__Tab pill__Tab-sm mx-1"
        :class="{ 'pill__Tab-active': trashFileTabActive }"
        href="/trash-file"
      >
        {{ $t('Files') }}
      </a>
    </nav>
  </div>

  <div 
    v-if="bcHomeStore.view"
    class="block md:hidden" 
  >
    <dropdown-heading v-if="bcHomeStore.views.length > 1">
      <template #heading>
        <h2 class="text-gray-800 text-sm">{{ header }}</h2>
      </template>
      <template #content>
        <nav class="p-0">
          <template v-for="item in bcHomeStore.views" :key="item.name">
            <a
              class="block py-1.5 px-3 text-gray-600"
              href="#"
              @click.prevent="bcHomeStore.view = item.name"
            >
              {{ item.title }}
            </a>
          </template>
        </nav>
      </template>
    </dropdown-heading>
    <h2
      v-else
      class="text-gray-800 text-sm"
    >
      {{ header }}
    </h2>
  </div>
</template>

<script>
import { mapStores } from 'pinia'
import useBcBreadCrumbsStore from '@/frontapp/store/bcBreadCrumbs'
import useBcFolderStore from '@/frontapp/store/bcFolder'
import useBcHomeStore from '@/frontapp/store/bcHome'
import useBcSearchStore from '@/frontapp/store/bcSearch'
import { useWindowSize } from '@vueuse/core'
import DocumentStore from '../mixins/DocumentStore'
const { width } = useWindowSize()

export default {
  mixins: [
    DocumentStore,
  ],
  props: {
    data: Array,
    controlName: String
  },
  data() {
    return {
      screenWidth: 0
    }
  },
  computed: {
    ...mapStores(useBcBreadCrumbsStore, useBcFolderStore, useBcHomeStore, useBcSearchStore),
    safeWidthZone() {
      return this.screenWidth - 225 // 225 right col
    },
    crumbs() {
      const totalItems = this.bcBreadCrumbsStore.crumbs.length
      const visibleCrumbs = Math.min(
        Math.floor(this.safeWidthZone / 200),
        totalItems
      )
      const weShow = visibleCrumbs - 2 // -2 = first + last

      let finalCrumbs = [
        this.bcBreadCrumbsStore.crumbs[0],
        ...this.bcBreadCrumbsStore.crumbs.slice(1, weShow + 1),
        this.bcBreadCrumbsStore.crumbs[totalItems - 1]
      ]
      // odstraneni duplicit, kdyz je tam malo polozek 1-2
      finalCrumbs = finalCrumbs.filter(
        (value, index, self) =>
          index ===
          self.findIndex((t) => t.url === value.url && t.title === value.title)
      )

      return finalCrumbs
    },
    header() {
      const truncateLimit = width.value / 15 - 2
      for (let index = 0; index < this.bcHomeStore.views.length; index++) {
        const element = this.bcHomeStore.views[index]
        if (element.name === this.bcHomeStore.view) {
          if (element.title.length <= truncateLimit) {
            return element.title
          } else {
            return element.title.slice(0, truncateLimit) + '...'
          }
        }
      }

      return null
    },
    mobileCrumb() {
      if (this.bcBreadCrumbsStore.crumbs.length === 1) {
        return {
          icon: 'home',
          title: this.bcBreadCrumbsStore.crumbs[0].title,
          url: this.bcBreadCrumbsStore.crumbs[0].url
        }
      } else {
        return {
          icon: 'chevron-left',
          title: this.bcBreadCrumbsStore.crumbs[this.bcBreadCrumbsStore.crumbs.length - 1].title,
          url: this.bcBreadCrumbsStore.crumbs[this.bcBreadCrumbsStore.crumbs.length - 2].url
        }
      }
    },
    icon() {
      if (this.$route.matched[0].name === 'BCFolder') {
        if (this.bcFolderStore.current?.hidden) {
          return 'eye-closed'
        } else if (this.bcFolderStore.current?.deleted) {
          return 'trash'
        } else {
          return 'eye-opened'
        }
      } else if (this.$route.matched[0].name === 'BCDocument') {
        if (this.bcDocumentStore?.edit) {
          return 'pen'
        } else if (this.bcFolderStore.current?.deleted) {
          return 'trash'
        } else if (
          !this.bcDocumentStore?.document?.hidden &&
          this.bcDocumentStore?.document?.bc_document_rev_id ===
            this.bcDocumentStore?.document?.published__bc_document_rev_id
        ) {
          return 'eye-opened'
        } else if (
          this.bcDocumentStore?.document?.hidden ||
          this.bcDocumentStore?.document?.bc_document_rev_id !==
            this.bcDocumentStore?.document?.published__bc_document_rev_id
        ) {
          return 'eye-closed'
        }
      }
      return false
    },
    iconColor() {
      if (this.icon === 'eye-opened') {
        return 'text-green-600'
      } else {
        return 'text-red-600'
      }
    },
    trashTabActive() {
      return ['BCTrash', 'BCTrashDocument'].includes(this.$route.matched[0].name)
    },
    trashFileTabActive() {
      return this.$route.matched[0].name === 'BCTrashFile'
    }
  },
  methods: {
    handleResize() {
      this.screenWidth = window.innerWidth
    },
    crumbClick(home) {
      if (home) {
        this.bcHomeStore.view = 'bc-name'
      }

      this.bcSearchStore.cancel()
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
    this.handleResize()
  },
  unmounted() {
    window.removeEventListener('resize', this.handleResize)
  },
  watch: {
    data: {
      handler(newVal) {
        this.bcBreadCrumbsStore.setBaseCrumbs(newVal)
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
.BreadCrumbs__Wrapper {
  min-width: 250px;
}

.BreadCrumbs__HomeIcon {
  margin-top: -2px
}

.Chunk {
  @apply truncate;
  max-width: 200px;
}
</style>
